import { AdmissionRules, CarePlanRules } from '@quromedical/business-rules';
import {
  EncounterDischargeReasonCode,
  EncounterInvalidateReasonCode,
  GroupSecurity,
} from '@quromedical/fhir-common';
import { AdmissionGetResponse } from '@quromedical/models';
import { useLinkTo } from '@react-navigation/native';
import { ConfirmationDialog } from 'components/input';
import {
  AdmissionCard,
  AdmissionInvalidateReason,
  AdmissionInlineUpdateCard,
  CreateAdmissionCarePlan,
  AdmissionDischargeReason,
  CreateAdmissionCarePlanProps,
  AdmissionDischargeReasonProps,
  AdmissionInvalidateReasonProps,
} from 'core/admission';
import { WithGroup } from 'core/auth';
import {
  BASE_SIZE,
  Button,
  Section,
  Skeleton,
  SkeletonProvider,
  Spacer,
  Visible,
} from 'design-system';
import { noOpAsync } from 'helpers';
import { useUserSession } from 'hooks';
import { useRevalidation } from 'hooks/useRevalidation';
import { useToggle } from 'hooks/useToggle';
import { AdmissionApi, PatientApi, CarePlanApi, PatientEndpoint } from 'integration/aggregate';
import React, { useCallback, useMemo, useState } from 'react';
import { strings } from 'strings';
import useSWR from 'swr';

type AdmissionActionType = 'discharge' | 'invalidate' | 'createCarePlan';

type AdmissionDischarge = { statusReason: EncounterDischargeReasonCode; periodEnd: string };
type AdmissionInvalidate = { statusReason: EncounterInvalidateReasonCode };
type AdmissionCarePlanCreate = { planDefinition: string; admissionId: string };

interface AdmissionAction<T extends AdmissionActionType> {
  type: T;
  data: T extends 'discharge'
    ? AdmissionDischarge
    : T extends 'invalidate'
      ? AdmissionInvalidate
      : AdmissionCarePlanCreate;
}

interface PatientAdmissionsTabProps {
  patientId: string;
}

const admissionApi = new AdmissionApi();
const patientApi = new PatientApi();
const carePlanApi = new CarePlanApi();

const Placeholder: React.FC = () => (
  <Section>
    <Skeleton>
      <AdmissionCard downloadFile={noOpAsync} />
    </Skeleton>
  </Section>
);

/**
 * Admissions are only editable if they are `in-progress`
 */
const isEditableAdmission = (admission: AdmissionGetResponse) =>
  admission.status === 'in-progress' || admission.status === 'onleave';

interface AdmissionListProps {
  admissions: AdmissionGetResponse[];
  patientId: string;
  canEditEncounter: boolean;
  canViewMedia: boolean;
  revalidate: () => Promise<void>;
  onAction: (type: 'discharge' | 'invalidate') => void;
  canInvalidate: boolean;
}

const AdmissionList: React.FC<AdmissionListProps> = ({
  admissions,
  patientId,
  canEditEncounter,
  canViewMedia,
  revalidate,
  onAction,
  canInvalidate,
}) => {
  return admissions.map((admission, index) => {
    const isEditable = isEditableAdmission(admission);
    const canEdit = canEditEncounter && isEditable;

    const pauseRule = AdmissionRules.AdmissionRules.validateAdmissionRequest({
      activeAdmissions: admissions,
      admissionType: 'pause-admission',
    });
    const resumeRule = AdmissionRules.AdmissionRules.validateAdmissionRequest({
      activeAdmissions: admissions,
      admissionType: 'resume-admission',
    });

    const showPauseButton = admission.status === 'in-progress' && pauseRule.type === 'success';
    const showResumeButton = admission.status === 'onleave' && resumeRule.type === 'success';

    return (
      <Section key={admission.id} hasBottomMargin={index === admissions.length - 1}>
        <Skeleton>
          <AdmissionInlineUpdateCard
            patientId={patientId}
            revalidate={revalidate}
            data={admission}
            canEdit={canEdit}
            canViewMedia={canViewMedia}
            onTertiarySubmit={() => onAction(canInvalidate ? 'invalidate' : 'discharge')}
            tertiaryButtonText={
              canInvalidate ? strings.AdmissionInvalidate : strings.PatientDischargeDialogButtonText
            }
            showSecondarySubmitButton={canEdit && (showPauseButton || showResumeButton)}
          />
        </Skeleton>
      </Section>
    );
  });
};

const getInitialActionData = (type: AdmissionActionType) => {
  const initialActionDataMap: {
    discharge: AdmissionDischarge;
    invalidate: AdmissionInvalidate;
    createCarePlan: AdmissionCarePlanCreate;
  } = {
    discharge: {
      statusReason: '' as EncounterDischargeReasonCode,
      periodEnd: '',
    },
    invalidate: {
      statusReason: '' as EncounterInvalidateReasonCode,
    },
    createCarePlan: {
      planDefinition: '',
      admissionId: '',
    },
  };
  return initialActionDataMap[type];
};

export const PatientAdmissionsTab: React.FC<PatientAdmissionsTabProps> = ({ patientId }) => {
  const linkTo = useLinkTo();
  const session = useUserSession();
  const revalidate = useRevalidation(patientId);

  const canViewMedia = session.hasAny([GroupSecurity.MediaRead]);
  const canEditEncounter = session.hasAny([GroupSecurity.EncounterUpdate]);

  const onCreatePress = useCallback(() => {
    linkTo({
      screen: 'Patient',
      params: {
        screen: 'View',
        params: {
          id: patientId,
          screen: 'Admit',
        },
      },
    });
  }, [linkTo, patientId]);

  const admissionFetcher = () => admissionApi.getAdmissions({ patient: patientId });
  const carePlanFetcher = () => carePlanApi.getCarePlans({ patientId });

  const swrKey = patientApi.getPatientSWRKey(patientId, PatientEndpoint.Admit);

  const { data: admissionData, isValidating } = useSWR(swrKey, admissionFetcher);
  const { data: carePlanData } = useSWR(`${patientId}-CarePlan`, carePlanFetcher);

  const [isDialogOpen, setDialogOpen] = useToggle(false);
  const [currentAction, setCurrentAction] = useState<AdmissionAction<AdmissionActionType>>();

  const admissions = useMemo(() => admissionData?.admissions || [], [admissionData?.admissions]);
  const carePlans = useMemo(() => carePlanData?.carePlans || [], [carePlanData?.carePlans]);

  const activeAdmission = useMemo(
    () => admissions.find(admission => admission.status === 'in-progress'),
    [admissions]
  );

  const { canCreateNewAdmission, canInvalidateAdmission, canCreateNewCarePlan } = useMemo(() => {
    return {
      canCreateNewAdmission: AdmissionRules.AdmissionRules.validateAdmissionRequest({
        activeAdmissions: admissions,
        admissionType: 'new-admission',
      }),
      canInvalidateAdmission: AdmissionRules.AdmissionRules.validateAdmissionRequest({
        activeAdmissions: admissions,
        admissionType: 'invalidate-admission',
      }),
      canCreateNewCarePlan: CarePlanRules.CarePlanRules.validateCarePlanRequest({
        carePlans,
        rule: 'new-care-plan',
      }),
    };
  }, [admissions, carePlans]);

  const handleDischarge = useCallback(
    async (data: AdmissionDischarge) => {
      await admissionApi.dischargePatient(patientId, data.statusReason, data.periodEnd);
      await revalidate();
    },
    [patientId, revalidate]
  );

  const handleInvalidate = useCallback(
    async (data: AdmissionInvalidate) => {
      await admissionApi.invalidateAdmission(patientId, data.statusReason);
      await revalidate();
    },
    [patientId, revalidate]
  );

  const handleCarePlanCreation = useCallback(
    async (data: AdmissionCarePlanCreate) => {
      if (!activeAdmission?.id) {
        return;
      }

      await carePlanApi.createCarePlan({
        admissionId: activeAdmission.id,
        patientId,
        planDefinition: data.planDefinition,
      });

      await revalidate();
    },
    [activeAdmission?.id, patientId, revalidate]
  );

  const canCreateAdmission = canCreateNewAdmission.type === 'success';
  const canInvalidate = canInvalidateAdmission.type === 'success';
  const canCreateCarePlan = canCreateNewCarePlan.type === 'success';

  const handleAction = useCallback(
    (type: AdmissionActionType) => {
      const data = getInitialActionData(type);
      setCurrentAction({ type, data });
      setDialogOpen();
    },
    [setDialogOpen]
  );

  type ActionComponentMap = {
    discharge: React.FC<AdmissionDischargeReasonProps>;
    invalidate: React.FC<AdmissionInvalidateReasonProps>;
    createCarePlan: React.FC<CreateAdmissionCarePlanProps>;
  };

  interface ActionConfig {
    title: string;
    message: string;
    buttonText: string;
    Component: ActionComponentMap[AdmissionActionType];
    action: () => Promise<void>;
    requiredGroups: GroupSecurity[];
  }

  const actionConfigs: Record<AdmissionActionType, ActionConfig> = {
    discharge: {
      title: strings.PatientDischargeDialogTitle,
      message: strings.PatientDischargeDialogMessage,
      buttonText: strings.PatientDischargeDialogButtonText,
      Component: AdmissionDischargeReason,
      action: async () => {
        if (currentAction?.type === 'discharge') {
          await handleDischarge(currentAction.data as AdmissionDischarge);
        }
      },
      requiredGroups: [GroupSecurity.EncounterUpdate],
    },
    invalidate: {
      title: strings.AdmissionInvalidateDialogTitle,
      message: strings.AdmissionInvalidateDialogMessage,
      buttonText: strings.AdmissionInvalidate,
      Component: AdmissionInvalidateReason,
      action: async () => {
        if (currentAction?.type === 'invalidate') {
          await handleInvalidate(currentAction.data as AdmissionInvalidate);
        }
      },
      requiredGroups: [GroupSecurity.EncounterUpdate],
    },
    createCarePlan: {
      title: strings.AdmissionCarePlanCreateTitle,
      message: strings.AdmissionCarePlanDialogMessage,
      buttonText: strings.CarePlanButtonTextCreate,
      Component: CreateAdmissionCarePlan,
      action: async () => {
        if (currentAction?.type === 'createCarePlan') {
          await handleCarePlanCreation(currentAction.data as AdmissionCarePlanCreate);
        }
      },
      requiredGroups: [GroupSecurity.CarePlanCreate],
    },
  };

  return (
    <SkeletonProvider loading={isValidating}>
      <Section isVisible={canCreateAdmission && !isValidating}>
        <Button text={strings.AdmissionButtonTextCreate} onPress={onCreatePress} />
      </Section>

      <Section isVisible={canCreateCarePlan && !isValidating && !canCreateAdmission}>
        <WithGroup groups={[GroupSecurity.CarePlanCreate]}>
          <Button
            text={strings.CarePlanButtonTextCreate}
            onPress={() => handleAction('createCarePlan')}
          />
        </WithGroup>
      </Section>

      <Visible if={!admissionData}>
        <Placeholder />
      </Visible>
      <AdmissionList
        admissions={admissions}
        patientId={patientId}
        canEditEncounter={canEditEncounter}
        canViewMedia={canViewMedia}
        revalidate={revalidate}
        canInvalidate={canInvalidate}
        onAction={type => handleAction(type)}
      />

      {currentAction && isDialogOpen && (
        <ConfirmationDialog
          onSubmit={actionConfigs[currentAction.type].action}
          buttonText={actionConfigs[currentAction.type].buttonText}
          title={actionConfigs[currentAction.type].title}
          message={actionConfigs[currentAction.type].message}
          status="status-critical"
          setDialogState={setDialogOpen}
        >
          {React.createElement(actionConfigs[currentAction.type].Component as any, {
            onChange: (data: typeof currentAction.data) =>
              setCurrentAction(prev => (prev ? { type: prev.type, data } : undefined)),
            initialData: currentAction.data,
          })}
          <Spacer height={BASE_SIZE[200]} />
        </ConfirmationDialog>
      )}
    </SkeletonProvider>
  );
};
